import { css, StyleSheet } from 'aphrodite'
import PropTypes from 'prop-types'
import React, { useState, useMemo, useEffect } from 'react'
import {
  COLOR_ERROR,
  COLOR_GRAY__600,
  COLOR_GRAY__800,
  COLOR_MUTED,
  COLOR_WHITE,
} from '../../lib/colors'
import UIInputError from './UIInputError'
import Autocomplete from 'react-autocomplete'
import GET_ALL_CITY from '../../graphql/Requests/queries/GetCity'
import { useLazyQuery } from '@apollo/client'
import { capitalize } from '../../utils/Utils'

const UIAutocompleteCity = React.forwardRef(function UIInput(
  {
    label,
    placeholder,
    message,
    errorMessage,
    kind = 'normal',
    dynamicStyle,
    onSelect,
    onChange,
    onBlur,
    cityValue = '',
  },
  ref
) {
  const [city, setCity] = useState('')
  const [isSuggestionCityOpened, setSuggestionCityOpened] = useState(false)

  const [loadCities, { data: cityList }] = useLazyQuery(GET_ALL_CITY, {
    fetchPolicy: 'cache-and-network',
  })

  const cities = useMemo(() => {
    if (!cityList) return []
    return cityList.cities.edges
  }, [cityList])

  useEffect(() => {
    if (cityValue) setCity(cityValue)
  }, [cityValue])

  const isInError = !!errorMessage
  const hasMessage = !!message && !isInError

  const onCityChange = (e) => {
    e.preventDefault()
    const city = e.target.value
    setCity(city)

    if (city.length === 0) {
      setSuggestionCityOpened(false)
    } else {
      setSuggestionCityOpened(true)
    }

    let filter = { name: { likeInsensitive: `${city}%` } }
    if (city.length > 2)
      if (city.indexOf('saint') !== -1) {
        filter = {
          or: [
            { and: { name: { likeInsensitive: `${city}%` } } },
            {
              and: {
                name: {
                  likeInsensitive: `${city
                    .toLowerCase()
                    .replace('saint', 'st')}%`,
                },
              },
            },
          ],
        }
      }

    loadCities({
      variables: {
        filter,
        first: 10,
        offset: 0,
      },
    })

    onChange(city)
  }

  const onCitySelect = (value, selected) => {
    setCity(capitalize(value))
    setSuggestionCityOpened(false)
    onSelect(value, selected)
  }

  return (
    <div className={css(styles[kind], dynamicStyle)}>
      <label className="uiInput">
        {!!label && (
          <>
            <span className={css(styles.label)}>{label}</span>
            <br />
          </>
        )}
        <Autocomplete
          getItemValue={(item) => item?.node?.name}
          items={cities}
          wrapperStyle={{ width: '100%' }}
          menuStyle={{ zIndex: 999, position: 'fixed' }}
          renderItem={(item, isHighlighted) => (
            <div
              key={item?.node?.id}
              style={{
                background: isHighlighted ? 'lightgray' : 'white',
              }}
            >
              {capitalize(item?.node?.name)} ({item?.node?.postcode})
            </div>
          )}
          value={city}
          onChange={onCityChange}
          onSelect={onCitySelect}
          open={isSuggestionCityOpened}
          inputProps={{
            ref,
            placeholder,
            onChange: onCityChange,
            onBlur,
          }}
          renderInput={({ ref, placeholder, onChange, ...props }) => {
            return (
              <input
                type="text"
                className={css(styles.input, isInError && styles.inputError)}
                placeholder={placeholder}
                ref={ref}
                onChange={onChange}
                autoComplete
                onMouse
                {...props}
              />
            )
          }}
        />
        {isInError && (
          <>
            <UIInputError errorMessage={errorMessage} />
          </>
        )}
        {hasMessage && (
          <>
            <span className={css(styles.message)}>{message}</span>
          </>
        )}
      </label>
    </div>
  )
})

const styles = StyleSheet.create({
  stack: {
    flexGrow: 1,
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid transparent',
    borderRadius: 5,
    backgroundColor: COLOR_WHITE,
    boxShadow: '0 1px 2px 0 #32324733',
    fontWeight: 500,
    fontSize: 15,
    color: COLOR_GRAY__800,
  },
  inputError: {
    borderColor: COLOR_ERROR,
    color: COLOR_ERROR,
  },
  message: {
    fontWeight: 400,
    fontSize: 11,
    color: COLOR_MUTED,
  },
  label: {
    display: 'inline-block',
    fontWeight: 400,
    fontSize: 14,
    color: COLOR_GRAY__600,
  },
})

UIAutocompleteCity.propTypes = {
  label: PropTypes.string,
  type: PropTypes.oneOf(['text', 'password', 'email', 'tel', 'date', 'file']),
  placeholder: PropTypes.string,
  message: PropTypes.string,
  errorMessage: PropTypes.string,
  kind: PropTypes.oneOf(['stack', null]),
  size: PropTypes.string,
  value: PropTypes.string,
  dynamicStyle: PropTypes.object,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  onBlur: PropTypes.func,
  cityValue: PropTypes.string,
}

export default UIAutocompleteCity
